import type { ReactElement, ReactNode } from 'react';

import { NavBar } from '~/components/layout/NavBar';
import { Footer } from '~/components/layout/Footer';
import { GtmLoader } from '~/components/analytics/GtmLoader';
import { CookieConsent } from '~/components/privacy/CookieConsent';

/**
 * Full page shell:
 *   <SkipLink>  →  <NavBar>  →  <main>  →  <Footer>  →  <GtmLoader>  →  <CookieConsent>
 *
 * The skip-link comes first in the DOM so keyboard users can bypass the nav.
 * The <main> has a negative tabIndex so the skip-link can programmatically
 * receive focus on activation without disrupting the natural tab order.
 *
 * GtmLoader and CookieConsent are rendered last so they don't shift layout.
 * Both are client-only side-effect components (GtmLoader returns null;
 * CookieConsent renders nothing during SSR).
 *
 * Scroll-behaviour (smooth) is declared in globals.css and honours
 * prefers-reduced-motion via a CSS @media override there.
 */
export function Layout(props: { children: ReactNode }): ReactElement {
  return (
    <>
      {/* Skip to main content — visible only on focus */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[var(--color-electric-blue)] focus:text-[var(--color-frost-white)] focus:rounded focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to content
      </a>

      <NavBar />

      <main id="main" tabIndex={-1}>
        {props.children}
      </main>

      <Footer />

      <GtmLoader />
      <CookieConsent />
    </>
  );
}
